import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { ConditionalDashboardRoute } from './components/auth/ConditionalDashboardRoute';

// Lazy load all pages for code splitting and performance optimization
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const TopUp = lazy(() => import('./pages/TopUp'));
const BMAccounts = lazy(() => import('./pages/BMAccounts'));
const PersonalAccounts = lazy(() => import('./pages/PersonalAccounts'));
const VerifiedBMService = lazy(() => import('./pages/VerifiedBMService'));
const VerifiedInstagram = lazy(() => import('./pages/VerifiedInstagram'));
const VerifiedFacebookPage = lazy(() => import('./pages/VerifiedFacebookPage'));
const VerifiedWhatsApp = lazy(() => import('./pages/VerifiedWhatsApp'));
const ClaimWarranty = lazy(() => import('./pages/ClaimWarranty'));
const ClaimWarrantyNew = lazy(() => import('./components/warranty/ClaimWarrantyNew'));
const WarrantyStatusDashboard = lazy(() => import('./components/warranty/WarrantyStatusDashboard'));
const APIDocumentation = lazy(() => import('./pages/APIDocumentation'));
const TutorialCenter = lazy(() => import('./pages/TutorialCenter'));


// Legal pages moved to main.tsx for standalone rendering (SEO optimization)

// Email verification pages moved to main.tsx for standalone rendering

// Admin pages
const UserManagement = lazy(() => import('./pages/UserManagement'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const TransactionManagement = lazy(() => import('./pages/admin/TransactionManagement'));
const WarrantyClaimManagement = lazy(() => import('./pages/admin/WarrantyClaimManagement'));
const TutorialManagement = lazy(() => import('./pages/admin/TutorialManagement'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const SystemSettings = lazy(() => import('./pages/admin/SystemSettings'));
const AuditLog = lazy(() => import('./pages/admin/AuditLog'));
const AnnouncementManagement = lazy(() => import('./pages/admin/AnnouncementManagement'));
const WelcomePopupManagement = lazy(() => import('./pages/admin/WelcomePopupManagement'));
const VerifiedBMManagement = lazy(() => import('./pages/admin/VerifiedBMManagement'));
const VerifiedInstagramAdmin = lazy(() => import('./pages/admin/VerifiedInstagramAdmin'));
const VerifiedFacebookPageAdmin = lazy(() => import('./pages/admin/VerifiedFacebookPageAdmin'));
const VerifiedWhatsAppAdmin = lazy(() => import('./pages/admin/VerifiedWhatsAppAdmin'));

/**
 * Loading component displayed while pages are being lazy loaded
 */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

/**
 * Member area routes configuration
 * All routes are protected and require authentication
 * Supports route parameters for filtering and navigation
 */
const MemberRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Root path - Conditional behavior based on auth status */}
        {/* Guest: Render Dashboard at "/" */}
        {/* Authenticated: Redirect to "/dashboard" */}
        <Route path="/" element={<ConditionalDashboardRoute />} />
        
        {/* Dashboard - Main overview page - Requires authentication */}
        <Route 
          path="dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Transaction History - Supports tab parameter for filtering */}
        <Route 
          path="riwayat-transaksi" 
          element={
            <ProtectedRoute>
              <TransactionHistory />
            </ProtectedRoute>
          } 
        />
        
        {/* Top Up - Balance recharge page */}
        <Route 
          path="top-up" 
          element={
            <ProtectedRoute>
              <TopUp />
            </ProtectedRoute>
          } 
        />
        
        {/* BM Accounts - Accessible to guests */}
        <Route 
          path="akun-bm" 
          element={<BMAccounts />} 
        />
        
        {/* Personal Accounts - Accessible to guests */}
        <Route 
          path="akun-personal" 
          element={<PersonalAccounts />} 
        />
        
        {/* Verified BM Service - Accessible to guests */}
        <Route 
          path="jasa-verified-bm" 
          element={<VerifiedBMService />} 
        />
        
        {/* Verified Instagram Service - Accessible to guests */}
        <Route 
          path="jasa-verified-ig" 
          element={<VerifiedInstagram />} 
        />
        
        {/* Verified Facebook Page Service - Accessible to guests */}
        <Route 
          path="jasa-verified-fp" 
          element={<VerifiedFacebookPage />} 
        />
        
        {/* Verified WhatsApp Service - Accessible to guests */}
        <Route 
          path="jasa-verified-wa" 
          element={<VerifiedWhatsApp />} 
        />
        
        {/* Warranty Claims */}
        <Route 
          path="claim-garansi" 
          element={
            <ProtectedRoute>
              <ClaimWarranty />
            </ProtectedRoute>
          } 
        />
        
        {/* New Warranty Claims System */}
        <Route 
          path="claim-garansi-new" 
          element={
            <ProtectedRoute>
              <ClaimWarrantyNew />
            </ProtectedRoute>
          } 
        />
        
        {/* Warranty Status Dashboard */}
        <Route 
          path="status-garansi" 
          element={
            <ProtectedRoute>
              <WarrantyStatusDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* API Documentation - Accessible to guests */}
        <Route 
          path="api" 
          element={<APIDocumentation />} 
        />
        
        {/* Tutorial Center - Accessible to guests */}
        <Route 
          path="tutorial" 
          element={<TutorialCenter />} 
        />
        

        
        {/* Email Verification Routes moved to main.tsx for standalone rendering */}
        
        {/* Legal Pages moved to main.tsx for standalone rendering without layout wrapper */}
        
        {/* Unauthorized Page */}
        <Route 
          path="unauthorized" 
          element={<Unauthorized />} 
        />
        
        {/* Admin Routes */}
        <Route 
          path="admin/dashboard" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/users" 
          element={
            <ProtectedRoute requiredRole="admin">
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/transactions" 
          element={
            <ProtectedRoute requiredRole="admin">
              <TransactionManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/claims" 
          element={
            <ProtectedRoute requiredRole="admin">
              <WarrantyClaimManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/tutorials" 
          element={
            <ProtectedRoute requiredRole="admin">
              <TutorialManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/products" 
          element={
            <ProtectedRoute requiredRole="admin">
              <ProductManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/settings" 
          element={
            <ProtectedRoute requiredRole="admin">
              <SystemSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/audit-logs" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AuditLog />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/announcements" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AnnouncementManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/welcome-popups" 
          element={
            <ProtectedRoute requiredRole="admin">
              <WelcomePopupManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/verified-bm" 
          element={
            <ProtectedRoute requiredRole="admin">
              <VerifiedBMManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/verified-ig" 
          element={
            <ProtectedRoute requiredRole="admin">
              <VerifiedInstagramAdmin />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/verified-fp" 
          element={
            <ProtectedRoute requiredRole="admin">
              <VerifiedFacebookPageAdmin />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="admin/verified-wa" 
          element={
            <ProtectedRoute requiredRole="admin">
              <VerifiedWhatsAppAdmin />
            </ProtectedRoute>
          } 
        />
        
        {/* Catch-all route - Redirect to dashboard for unknown paths */}
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </Suspense>
  );
};

export default MemberRoutes;
